import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Target,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Save,
  RefreshCw,
  FileText } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell } from
'recharts';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const BUDGET_CATEGORIES = [
{ value: 'Product Sales', label: 'Product Sales', type: 'revenue' },
{ value: 'Service Revenue', label: 'Service Revenue', type: 'revenue' },
{ value: 'Investment Income', label: 'Investment Income', type: 'revenue' },
{ value: 'Other Revenue', label: 'Other Revenue', type: 'revenue' },
{ value: 'Payroll', label: 'Payroll', type: 'expense' },
{ value: 'Operations', label: 'Operations', type: 'expense' },
{ value: 'Marketing', label: 'Marketing', type: 'expense' },
{ value: 'Technology', label: 'Technology', type: 'expense' },
{ value: 'Travel', label: 'Travel', type: 'expense' },
{ value: 'Professional Services', label: 'Professional Services', type: 'expense' },
{ value: 'Equipment', label: 'Equipment', type: 'expense' },
{ value: 'Loan Payments', label: 'Loan Payments', type: 'expense' }];


const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

export const BudgetManagement = () => {const { t } = useTranslation("finance");
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    budget_type: 'expense',
    amount: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    notes: ''
  });

  const fetchBudgets = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/finance/budgets?year=${selectedYear}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setBudgets(data.budgets || []);
      }
    } catch (err) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/finance/reports/cash-flow/budget-comparison?period=${selectedPeriod}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setComparison(data);
      }
    } catch (err) {
      console.error('Failed to fetch comparison');
    }
  };

  useEffect(() => {
    fetchBudgets();
    fetchComparison();
  }, [selectedYear, selectedPeriod]);

  const handleSubmit = async () => {
    if (!formData.category || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingBudget ?
      `${API_URL}/api/finance/budgets/${editingBudget.id}` :
      `${API_URL}/api/finance/budgets`;

      const method = editingBudget ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        })
      });

      if (res.ok) {
        toast.success(editingBudget ? 'Budget updated!' : 'Budget created!');
        setShowAddModal(false);
        setEditingBudget(null);
        resetForm();
        fetchBudgets();
        fetchComparison();
      } else {
        toast.error('Failed to save budget');
      }
    } catch (err) {
      toast.error('Error saving budget');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this budget entry?')) return;

    try {
      const res = await fetch(`${API_URL}/api/finance/budgets/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Budget deleted');
        fetchBudgets();
        fetchComparison();
      }
    } catch (err) {
      toast.error('Failed to delete budget');
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      category: budget.category,
      budget_type: budget.budget_type,
      amount: budget.amount.toString(),
      month: budget.month,
      year: budget.year,
      notes: budget.notes || ''
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      budget_type: 'expense',
      amount: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      notes: ''
    });
  };

  const totalRevenueBudget = budgets.filter((b) => b.budget_type === 'revenue').reduce((sum, b) => sum + b.amount, 0);
  const totalExpenseBudget = budgets.filter((b) => b.budget_type === 'expense').reduce((sum, b) => sum + b.amount, 0);

  const chartData = comparison?.categories?.slice(0, 8).map((c) => ({
    category: c.category.length > 12 ? c.category.slice(0, 12) + '...' : c.category,
    actual: c.actual_revenue + c.actual_expenses,
    budget: c.budget_revenue + c.budget_expenses
  })) || [];

  const pieData = budgets.reduce((acc, b) => {
    const existing = acc.find((a) => a.name === b.category);
    if (existing) {
      existing.value += b.amount;
    } else {
      acc.push({ name: b.category, value: b.amount, type: b.budget_type });
    }
    return acc;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="budget-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("budgetManagement", "Budget Management")}</h1>
          <p className="text-muted-foreground mt-1">{t("setTrackAnd", "Set, track, and manage budgets for revenue and expenses")}

          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((y) =>
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => {fetchBudgets();fetchComparison();}} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button onClick={() => {resetForm();setEditingBudget(null);setShowAddModal(true);}} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />{t("addBudget", "Add Budget")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("revenueBudget", "Revenue Budget")}</p>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              ${totalRevenueBudget.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("expenseBudget", "Expense Budget")}</p>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">
              ${totalExpenseBudget.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("netBudget", "Net Budget")}</p>
              <Target className="h-4 w-4 text-blue-500" />
            </div>
            <p className={`text-2xl font-bold ${totalRevenueBudget - totalExpenseBudget >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              ${(totalRevenueBudget - totalExpenseBudget).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("budgetItems", "Budget Items")}</p>
              <FileText className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {budgets.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="budgets" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="budgets" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("budgetItems", "Budget Items")}

          </TabsTrigger>
          <TabsTrigger value="comparison" className="gap-2">
            <Target className="h-4 w-4" />{t("budgetVsActual", "Budget vs Actual")}

          </TabsTrigger>
          <TabsTrigger value="analysis" className="gap-2">
            <TrendingUp className="h-4 w-4" />{t("analysis", "Analysis")}

          </TabsTrigger>
        </TabsList>

        {/* Budget Items Tab */}
        <TabsContent value="budgets" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Revenue Budgets */}
            <Card className="card-elevated">
              <CardHeader className="pb-3 border-b bg-emerald-50/50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />{t("revenueBudgets", "Revenue Budgets")}

                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {budgets.filter((b) => b.budget_type === 'revenue').map((budget, idx) =>
                  <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                      <div>
                        <p className="font-medium">{budget.category}</p>
                        <p className="text-xs text-muted-foreground">
                          {budget.month ? `Month ${budget.month}` : 'Annual'} • {budget.year}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-emerald-600 font-semibold">
                          ${budget.amount.toLocaleString()}
                        </span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(budget)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(budget.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {budgets.filter((b) => b.budget_type === 'revenue').length === 0 &&
                  <div className="p-8 text-center text-muted-foreground">{t("noRevenueBudgets", "No revenue budgets set")}

                  </div>
                  }
                </div>
              </CardContent>
            </Card>

            {/* Expense Budgets */}
            <Card className="card-elevated">
              <CardHeader className="pb-3 border-b bg-red-50/50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-600" />{t("expenseBudgets", "Expense Budgets")}

                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {budgets.filter((b) => b.budget_type === 'expense').map((budget, idx) =>
                  <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                      <div>
                        <p className="font-medium">{budget.category}</p>
                        <p className="text-xs text-muted-foreground">
                          {budget.month ? `Month ${budget.month}` : 'Annual'} • {budget.year}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-red-600 font-semibold">
                          ${budget.amount.toLocaleString()}
                        </span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(budget)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(budget.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {budgets.filter((b) => b.budget_type === 'expense').length === 0 &&
                  <div className="p-8 text-center text-muted-foreground">{t("noExpenseBudgets", "No expense budgets set")}

                  </div>
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget vs Actual Tab */}
        <TabsContent value="comparison" className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">{t("weekly", "Weekly")}</SelectItem>
                <SelectItem value="monthly">{t("monthly", "Monthly")}</SelectItem>
                <SelectItem value="quarterly">{t("quarterly", "Quarterly")}</SelectItem>
                <SelectItem value="yearly">{t("yearly", "Yearly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {comparison &&
          <>
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("actualRevenue", "Actual Revenue")}</p>
                    <p className="text-lg font-bold text-emerald-600">${(comparison.totals?.actual_revenue || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("budgetRevenue", "Budget Revenue")}</p>
                    <p className="text-lg font-bold text-blue-600">${(comparison.totals?.budget_revenue || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("actualExpenses", "Actual Expenses")}</p>
                    <p className="text-lg font-bold text-red-600">${(comparison.totals?.actual_expenses || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("budgetExpenses", "Budget Expenses")}</p>
                    <p className="text-lg font-bold text-amber-600">${(comparison.totals?.budget_expenses || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("budgetVsActual", "Budget vs Actual by Category")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => `$${v?.toLocaleString()}`} />
                        <Legend />
                        <Bar dataKey="actual" name="Actual" fill="#10b981" />
                        <Bar dataKey="budget" name="Budget" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Table */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("varianceAnalysis", "Variance Analysis")}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="py-2 px-3 text-left">{t("category", "Category")}</th>
                        <th className="py-2 px-3 text-right">{t("actualRev", "Actual Rev.")}</th>
                        <th className="py-2 px-3 text-right">{t("budgetRev", "Budget Rev.")}</th>
                        <th className="py-2 px-3 text-right">{t("var", "Var %")}</th>
                        <th className="py-2 px-3 text-right">{t("actualExp", "Actual Exp.")}</th>
                        <th className="py-2 px-3 text-right">{t("budgetExp", "Budget Exp.")}</th>
                        <th className="py-2 px-3 text-right">{t("var", "Var %")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.categories?.map((c, idx) =>
                    <tr key={idx} className="border-t">
                          <td className="py-2 px-3">{c.category}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">${c.actual_revenue?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-blue-600">${c.budget_revenue?.toLocaleString()}</td>
                          <td className={`py-2 px-3 text-right ${c.revenue_variance_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {c.revenue_variance_pct > 0 ? '+' : ''}{c.revenue_variance_pct}%
                          </td>
                          <td className="py-2 px-3 text-right text-red-600">${c.actual_expenses?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-amber-600">${c.budget_expenses?.toLocaleString()}</td>
                          <td className={`py-2 px-3 text-right ${c.expense_variance_pct <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {c.expense_variance_pct > 0 ? '+' : ''}{c.expense_variance_pct}%
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          }
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Budget Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>{t("budgetDistribution", "Budget Distribution")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}>
                        
                        {pieData.map((entry, index) =>
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.type === 'revenue' ? '#10b981' : COLORS[index % COLORS.length]} />

                        )}
                      </Pie>
                      <Tooltip formatter={(v) => `$${v?.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Budget Tips */}
            <Card>
              <CardHeader>
                <CardTitle>{t("budgetInsights", "Budget Insights")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="font-medium text-emerald-800">{t("revenueTarget", "Revenue Target")}</p>
                  <p className="text-sm text-emerald-600">{t("yourAnnualRevenue", "Your annual revenue budget is $")}
                    {totalRevenueBudget.toLocaleString()}{t("monthlyTarget", ". \n                    Monthly target: $")}
                    {(totalRevenueBudget / 12).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="font-medium text-red-800">{t("expenseControl", "Expense Control")}</p>
                  <p className="text-sm text-red-600">{t("yourAnnualExpense", "Your annual expense budget is $")}
                    {totalExpenseBudget.toLocaleString()}{t("monthlyLimit", ". \n                    Monthly limit: $")}
                    {(totalExpenseBudget / 12).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800">{t("netTarget", "Net Target")}</p>
                  <p className="text-sm text-blue-600">{t("targetNetProfit", "Target net profit: $")}
                    {(totalRevenueBudget - totalExpenseBudget).toLocaleString()} 
                    ({((totalRevenueBudget - totalExpenseBudget) / totalRevenueBudget * 100 || 0).toFixed(1)}{t("margin", "% margin)")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Budget Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBudget ? 'Edit Budget' : 'Add New Budget'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t("budgetType", "Budget Type")}</label>
              <Select
                value={formData.budget_type}
                onValueChange={(v) => setFormData({ ...formData, budget_type: v, category: '' })}>
                
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">{t("revenue", "Revenue")}</SelectItem>
                  <SelectItem value="expense">{t("expense", "Expense")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t("category", "Category")}</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}>
                
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_CATEGORIES.filter((c) => c.type === formData.budget_type).map((cat) =>
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t("amountUsd", "Amount (USD)")}</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="w-full mt-1 px-3 py-2 border rounded-md" />
              
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t("monthOptional", "Month (optional)")}</label>
                <Select
                  value={formData.month?.toString() || 'annual'}
                  onValueChange={(v) => setFormData({ ...formData, month: v === 'annual' ? null : parseInt(v) })}>
                  
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Annual" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">{t("annual", "Annual")}</SelectItem>
                    {[...Array(12)].map((_, i) =>
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t("year", "Year")}</label>
                <Select
                  value={formData.year.toString()}
                  onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}>
                  
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) =>
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t("notesOptional", "Notes (optional)")}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Budget notes..."
                className="w-full mt-1 px-3 py-2 border rounded-md h-20" />
              
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleSubmit} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
              <Save className="h-4 w-4" />
              {editingBudget ? 'Update' : 'Create'}{t("budget", "Budget")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default BudgetManagement;